import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet } from '@/lib/api';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Shield, LogOut, ChevronDown, Home, Sparkles, Upload, Users, Crown, Zap, Star, Compass, Check, UserPlus, Palette, AlertTriangle, Gauge, BookMarked, GraduationCap, CircleUserRound } from 'lucide-react';
import ReferEarnPill from '@/components/ReferEarnPill';
import { activateChildProfile, fetchChildProfiles, fetchThemePresets, themeBackgroundUrl } from '@/lib/childProfiles';
import AddChildDialog from '@/components/profile/AddChildDialog';
import { sameName } from '@/lib/nameKey';
import { useScreenTip } from '@/hooks/useScreenTip';
import { HELP } from '@/lib/helpText';
import ThemeDialog from '@/components/profile/ThemeDialog';
import NotificationBell from '@/components/NotificationBell';
import ParentDashboardDialog from '@/components/premium/ParentDashboardDialog';
import { TreasureChest } from '@/components/common/TreasureChest';
import AdminTasksButton from '@/components/admin/AdminTasksButton';

export default function WebLayout({ children, activeSubject, setActiveSubject }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoadError, setSubjectsLoadError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [topics, setTopics] = useState([]);
  const [activeTopic, setActiveTopic] = useState('');
  const [chatResetKey, setChatResetKey] = useState(0);
  const [visibleTiers, setVisibleTiers] = useState(null);
  const [growthSubUrl, setGrowthSubUrl] = useState('');
  const [trial, setTrial] = useState({ label: '', short: '', headline: 'Free for Every Student', chip: 'Free', bannerEnabled: true });
  // Treasure chest in the header — shakes while unopened coins are inside.
  const [chest, setChest] = useState({ coins: 0, shake: false });
  // Parent accounts carry no board/class of their own — those live on the
  // linked child. Resolve them so the header shows the child's real board and
  // class instead of a blank "Board" and a fabricated "Class 10".
  const [childCtx, setChildCtx] = useState(null);
  const autoPickedRef = useRef(false);
  const [roster, setRoster] = useState(null);   // child profiles + limits + charge notice
  const [addChildOpen, setAddChildOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [dashOpen, setDashOpen] = useState(false);
  // Study load is a safety signal — the avatar carries it so a parent cannot
  // miss that their child is being over-planned.
  const [planRisk, setPlanRisk] = useState('green');

  const loadRoster = () => fetchChildProfiles().then(setRoster).catch(() => setRoster(null));
  useEffect(() => { if (user) loadRoster(); /* eslint-disable-next-line */ }, [user?.user_id]);

  // Limited-time scenes only work if kids know they exist, so Chirpy says so
  // once per season (the tip key carries the season, so each one re-announces).
  const [seasonal, setSeasonal] = useState(null);
  useEffect(() => {
    if (!user) return;
    fetchThemePresets().then(d => setSeasonal(d.seasonal || null)).catch(() => setSeasonal(null));
  }, [user?.user_id]);
  useScreenTip(
    `season-${seasonal?.key || 'none'}`,
    seasonal ? HELP.tips.seasonalTheme
      .replace('{label}', seasonal.label)
      .replace('{occasion}', seasonal.occasion)
      .replace('{days}', seasonal.days_left) : '',
    !!seasonal,
  );

  const activeProfile = (roster?.profiles || []).find(p => p.is_active) || null;
  // Switching child must change the WHOLE view, so board/class come from the
  // active profile first and only then fall back to the account itself.
  const theme = activeProfile?.theme || roster?.self?.theme || null;

  const switchChild = async (profileId) => {
    try {
      await activateChildProfile(profileId);
      // The new child has a different board/class, so the remembered subject
      // and chapter no longer apply.
      ['gv_active_subject', 'gv_active_topic'].forEach(k => localStorage.removeItem(k));
      setActiveSubject(''); setActiveTopic(''); setTopics([]); setSubjects([]);
      autoPickedRef.current = false;
      await loadRoster();
      setReloadKey(k => k + 1);
    } catch (e) { /* menu stays open, nothing switched */ }
  };

  useEffect(() => {
    if (!user || user.class_level || user.role !== 'parent') return;
    apiGet('/parent/children')
      .then(d => {
        const kid = (d?.children || [])[0];
        if (kid) setChildCtx({ board: kid.child_board || '', class_level: kid.child_class || '' });
      })
      .catch(() => { /* header falls back to a neutral label */ });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    apiGet('/premium/v1/missions/today')
      .then(d => setPlanRisk(d?.risk || 'green'))
      .catch(() => setPlanRisk('green'));   // planner off or not entitled — stay quiet
  }, [user]);


  // Text that sits straight on the artwork must follow the theme, otherwise
  // dark slate headings vanish on Monsoon/Space. Cards keep their own colours.
  useEffect(() => {
    const b = document.body;
    if (theme) {
      b.dataset.themeDark = theme.dark ? 'true' : 'false';
      b.style.setProperty('--theme-ink', theme.ink || '#0f172a');
    } else {
      delete b.dataset.themeDark;
      b.style.removeProperty('--theme-ink');
    }
    return () => { delete b.dataset.themeDark; b.style.removeProperty('--theme-ink'); };
  }, [theme?.preset, theme?.dark, theme?.ink]);

  // The auto-pick below runs several awaits deep; it must be able to see the
  // CURRENT selection, not the one captured when the effect started.
  const activeSubjectRef = useRef(activeSubject);
  useEffect(() => { activeSubjectRef.current = activeSubject; }, [activeSubject]);

  const ctxBoard = activeProfile?.board || user?.board || childCtx?.board || '';
  const ctxClass = activeProfile?.class_level || user?.class_level || childCtx?.class_level || '';
  const pendingTopicRef = useRef(null);

  useEffect(() => {
    apiGet('/public/settings').then(d => {
      setVisibleTiers(d?.visible_tiers || {});
      const pc = d?.payment_config || {};
      setGrowthSubUrl(pc.growth_subscription_enabled && pc.growth_subscription_url ? pc.growth_subscription_url : '');
      setTrial({
        label: pc.trial_end_label || '',
        short: pc.trial_end_short || '',
        headline: pc.trial_headline || 'Free for Every Student',
        chip: pc.trial_chip || 'Free',
        bannerEnabled: pc.trial_banner_enabled !== false,
      });
    }).catch(() => setVisibleTiers({}));
  }, []);

  // Called by HomePage when loading a previous session to sync header dropdowns
  const onSessionLoad = (rawSubject, topic) => {
    // An explicit choice (a saved session, or Exam Mode opening the chapter its
    // plan asks for) always beats the alphabetical auto-pick, even if the
    // auto-pick's chapter probe is still in flight.
    autoPickedRef.current = true;
    pendingTopicRef.current = topic;
    // Use the dropdown's own spelling of the subject when it's a variant.
    const subject = subjects.find(s => sameName(s.name, rawSubject))?.name || rawSubject;
    if (!sameName(subject, activeSubject)) {
      setActiveSubject(subject);
    } else {
      // Subject is already correct, just update topic directly
      setActiveTopic(topics.find(t => sameName(t.name, topic))?.name || topic);
      pendingTopicRef.current = null;
    }
  };

  // Load subjects based on class AND board.
  //
  // Production bug fix: the previous version did `.catch(() => {})` which
  // silently swallowed every failure (transient network blip, 5xx, CORS) and
  // left `subjects` permanently empty — the dropdown then showed nothing even
  // after a refresh. We now:
  //   1. retry once after 1.5s on failure (most blips clear inside that window)
  //   2. on a second failure, set `subjectsLoadError` so the dropdown renders
  //      a "Couldn't load subjects · Retry" item the user can click to retry
  //   3. trigger via `reloadKey` so the manual retry actually re-runs the effect
  //   4. log the error to console so DevTools shows what happened
  useEffect(() => {
    let cancelled = false;
    if (!ctxClass) return undefined;
    const boardParam = ctxBoard ? `&board=${encodeURIComponent(ctxBoard)}` : '';
    const url = `/onboarding/subjects?class_level=${ctxClass}${boardParam}`;
    const loadOnce = () => apiGet(url);
    const run = async () => {
      setSubjectsLoadError(null);
      try {
        let d;
        try {
          d = await loadOnce();
        } catch (e1) {
          // Transient retry — sleep 1.5s and try again
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.warn('[WebLayout] subjects load failed, retrying in 1.5s:', e1?.message || e1);
          await new Promise(r => setTimeout(r, 1500));
          if (cancelled) return;
          d = await loadOnce();
        }
        if (cancelled) return;
        // Alphabetical so the pre-selected subject is predictable rather
        // than whatever order the API happened to return.
        const arr = [...(d?.subjects || [])].sort((a, b) => a.name.localeCompare(b.name));
        setSubjects(arr);
        if (!activeSubject && arr.length > 0 && !autoPickedRef.current) {
          autoPickedRef.current = true;
          // Landing on a subject with an empty chapter list makes the app look
          // broken on the very first screen, so walk alphabetically until we
          // find one that actually has chapters uploaded.
          const boardQ = ctxBoard ? `&board=${encodeURIComponent(ctxBoard)}` : '';
          (async () => {
            for (const sub of arr.slice(0, 8)) {
              if (cancelled) return;
              if (activeSubjectRef.current) return;   // something real got picked meanwhile
              try {
                const t = await apiGet(`/onboarding/topics?subject=${encodeURIComponent(sub.name)}&class_level=${ctxClass}${boardQ}`);
                if (cancelled || activeSubjectRef.current) return;
                if ((t?.topics || []).length > 0) { setActiveSubject(sub.name); return; }
              } catch { /* try the next subject */ }
            }
            if (!cancelled && !activeSubjectRef.current) setActiveSubject(arr[0].name);
          })();
        }
      } catch (e2) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[WebLayout] subjects load failed after retry:', e2?.message || e2);
        setSubjectsLoadError(e2?.message || 'load_failed');
      }
    };
    run();
    return () => { cancelled = true; };
  }, [ctxClass, ctxBoard, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load topics when subject changes (board-aware). Same retry-once pattern as
  // subjects above — silent .catch was hiding production failures.
  useEffect(() => {
    let cancelled = false;
    if (!activeSubject || !ctxClass) return undefined;
    const boardParam = ctxBoard ? `&board=${encodeURIComponent(ctxBoard)}` : '';
    const url = `/onboarding/topics?subject=${encodeURIComponent(activeSubject)}&class_level=${ctxClass}${boardParam}`;
    const loadOnce = () => apiGet(url);
    const run = async () => {
      try {
        let d;
        try { d = await loadOnce(); }
        catch (e1) {
          if (cancelled) return;
          await new Promise(r => setTimeout(r, 1500));
          if (cancelled) return;
          d = await loadOnce();
        }
        if (cancelled) return;
        const arr = [...(d?.topics || [])].sort((a, b) => a.name.localeCompare(b.name));
        setTopics(arr);
        const pending = pendingTopicRef.current;
        if (pending) {
          // A loaded chat decides the chapter — even if the list spells it differently.
          setActiveTopic(arr.find(t => sameName(t.name, pending))?.name || pending);
        } else {
          setActiveTopic(arr[0]?.name || '');
        }
        pendingTopicRef.current = null;
      } catch (e2) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[WebLayout] topics load failed after retry:', e2?.message || e2);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeSubject, ctxClass, ctxBoard, reloadKey]);

  useEffect(() => {
    const refresh = () => setReloadKey(k => k + 1);
    window.addEventListener('school-curriculum-changed', refresh);
    return () => window.removeEventListener('school-curriculum-changed', refresh);
  }, []);

  // Phase 2.70 — persist the top-bar selections so any page (Gamify,
  // Flashcards, Quiz, Library) can scope its content to the chapter the
  // student is currently studying, without needing route props or context
  // plumbing. Cleared when the value is empty so a logout/reset starts
  // fresh.
  useEffect(() => {
    try {
      if (activeSubject) window.localStorage.setItem('gv_active_subject', activeSubject);
      else window.localStorage.removeItem('gv_active_subject');
    } catch { /* ignore quota / private-mode errors */ }
  }, [activeSubject]);
  useEffect(() => {
    try {
      if (activeTopic) window.localStorage.setItem('gv_active_topic', activeTopic);
      else window.localStorage.removeItem('gv_active_topic');
    } catch { /* ignore */ }
  }, [activeTopic]);

  // Iter 61 (Jun 2026) — when the student is mid-puzzle (/gamify/play/:id),
  // changing Subject/Chapter must NOT silently swap context behind the live
  // puzzle. Confirm first; on "Change" we apply the new selection and return
  // Home, on "Continue" we keep them on the puzzle untouched.
  const [pendingNav, setPendingNav] = useState(null); // {type:'subject'|'chapter', value}
  const onPuzzlePlay = () => (location.pathname || '').startsWith('/gamify/play/');

  const applySubjectChange = (newSubject) => {
    setActiveSubject(newSubject);
    setActiveTopic('');
    setChatResetKey(k => k + 1);
  };
  const applyTopicChange = (newTopic) => {
    setActiveTopic(newTopic);
    setChatResetKey(k => k + 1);
  };

  const handleSubjectChange = (newSubject) => {
    if (newSubject === activeSubject) return;
    if (onPuzzlePlay()) { setPendingNav({ type: 'subject', value: newSubject }); return; }
    applySubjectChange(newSubject);
  };

  const handleTopicChange = (newTopic) => {
    if (newTopic === activeTopic) return;
    if (onPuzzlePlay()) { setPendingNav({ type: 'chapter', value: newTopic }); return; }
    applyTopicChange(newTopic);
  };

  const confirmPendingNav = () => {
    const p = pendingNav;
    setPendingNav(null);
    if (!p) return;
    if (p.type === 'subject') applySubjectChange(p.value);
    else applyTopicChange(p.value);
    navigate('/home');
  };

  const handleGoHome = () => {
    setChatResetKey(k => k + 1); // Force fresh chat state
    navigate('/home');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  // Iter 60 (Jul 2026) — Ribbon consolidation.
  // Dashboard / Graphics / Flashcards / Quiz / Gamify were cluttering the
  // top nav (5 icons + Home + Admin + Content + ReferEarn + Tier chip).
  // Rolled them into a single "My Gurukul" hub → clicking navigates to
  // `/my-gurukul` which renders colourful cards for each destination
  // (matches the visual language of the Gamify subject cards).
  const navItems = [
    { id: 'home', label: 'Home', icon: Home, path: '/home' },
    { id: 'my-gurukul', label: 'My Gurukul', icon: Compass, path: '/my-gurukul' },
    { id: 'veda-coins', label: 'My Veda Coins', icon: TreasureChest, path: '/my-veda-coins', chest: true },
  ];
  if (user?.role === 'admin') {
    navItems.push({ id: 'admin', label: 'Admin', icon: Shield, path: '/admin' });
  }
  if (user?.role === 'admin' || user?.role === 'support_staff') {
    navItems.push({ id: 'staff', label: 'Content', icon: Upload, path: '/staff' });
  }
  if (user?.role === 'parent') {
    navItems.push({ id: 'parent', label: 'My Children', icon: Users, path: '/parent' });
  }

  // Chest refresh: on mount and whenever the route changes, so coins earned on
  // the puzzle page show up the moment the student comes back.
  useEffect(() => {
    let alive = true;
    const refresh = () => apiGet('/coins/chest')
      .then(d => { if (alive) setChest({ coins: d.coins || 0, shake: !!d.shake }); })
      .catch(() => {});
    refresh();
    window.addEventListener('gv:chest-opened', refresh);
    return () => { alive = false; window.removeEventListener('gv:chest-opened', refresh); };
  }, [location.pathname]);

  const getActiveNav = () => {
    const p = location.pathname;
    if (p.startsWith('/admin')) return 'admin';
    if (p.startsWith('/my-veda-coins')) return 'veda-coins';
    if (p.startsWith('/staff')) return 'staff';
    if (p.startsWith('/parent')) return 'parent';
    // Iter 60 — every consolidated destination lights up "My Gurukul".
    if (
      p.startsWith('/my-gurukul') ||
      p.startsWith('/dashboard') ||
      p.startsWith('/gallery') ||
      p.startsWith('/flashcards') ||
      p.startsWith('/library') ||
      p.startsWith('/quiz') ||
      p.startsWith('/gamify')
    ) return 'my-gurukul';
    return 'home';
  };

  const activeNav = getActiveNav();

  // Shorten board name for header display
  const shortBoardName = (name) => {
    if (!name) return 'Board';
    const n = name.toLowerCase();
    if (n.includes('cbse')) return 'CBSE';
    if (n.includes('icse') || n.includes('cisce')) return 'ICSE';
    if (n.includes('nios')) return 'NIOS';
    if (n.includes('international baccalaureate') || n.includes(' ib ') || n.startsWith('ib ')) return 'IB';
    if (n.includes('cambridge') || n.includes('igcse')) return 'IGCSE';
    // State boards: extract state abbreviation
    const stateMatch = name.match(/^(\w+)/);
    if (n.includes('state board') || n.includes('bse')) return stateMatch ? `${stateMatch[1]} Board` : name.slice(0, 12);
    return name.length > 15 ? name.slice(0, 12) + '...' : name;
  };

  return (
    <TooltipProvider>
      {/* Theme canvas — per-child. The photo sits behind the app at the
          server-fixed opacity so studying always stays readable. */}
      <div className="fixed inset-0 -z-10 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="theme-canvas"
        style={{ background: theme?.bg || '#F8F9FC' }}>
        {/* Curated artwork shows at full strength; a parent's own photo is held
            back to 40% because we can't vouch for how busy it is. */}
        {theme?.background_path ? (
          <div className="absolute inset-0 bg-center bg-cover" data-testid="theme-photo-layer"
            style={{
              backgroundImage: `url("${themeBackgroundUrl(theme.background_path)}")`,
              opacity: theme.opacity ?? 0.4,
            }} />
        ) : theme?.image ? (
          <div className="absolute inset-0 bg-center bg-cover animate-[themeIn_900ms_ease-out]"
            data-testid="theme-art-layer"
            style={{ backgroundImage: `url("${theme.image}")` }} />
        ) : null}
        {/* Readability scrim — server-controlled, so the picture can never
            drown the studying. */}
        {theme && (
          <div className="absolute inset-0 backdrop-blur-[1px]" data-testid="theme-scrim"
            style={{ background: theme.background_path ? 'rgba(255,255,255,0.25)' : theme.scrim }} />
        )}
      </div>
      <div className={`min-h-screen flex flex-col ${theme ? 'bg-transparent' : 'bg-[#F8F9FC]'}`} data-testid="web-layout" data-gv-themed={theme ? 'true' : undefined}>
        {/* Top Header Bar */}
        <header className="bg-white border-b border-slate-100 px-3 sm:px-5 lg:px-6 py-2 sm:py-3 flex items-center gap-2 sm:gap-3 flex-wrap flex-shrink-0" data-testid="top-header">
          {/* Logo — Iter 60i (Aug 2026): compact single-row layout at
              every breakpoint below 2xl. The 3-line stacked tagline
              (name + subtitle + Beta pill) only surfaces on very wide
              (2xl+) displays. This keeps the header a single row at
              typical 100% zoom on 1440–1920px screens so students see
              greeting + topic chips + composer all on the first fold. */}
          <div className="flex items-center gap-2 sm:gap-2.5 mr-2 sm:mr-3 lg:mr-4 cursor-pointer flex-shrink-0" onClick={handleGoHome} data-testid="header-logo">
            <img src="/brand/logo.png" alt="Gurukul Veda" className="w-11 h-11 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 object-contain" />
            {/* 2xl+ (1536px+) — full tagline + Beta pill */}
            <div className="hidden 2xl:block">
              <p className="font-heading text-base font-bold text-slate-900 leading-tight">Gurukul Veda</p>
              <p className="text-[10px] text-slate-400 font-body leading-tight">Your Personal study companion</p>
              {trial.bannerEnabled !== false && (
              <p
                data-testid="beta-badge-header"
                className="mt-0.5 inline-block text-[9px] font-heading font-bold tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px]"
                title={`${trial.headline} — every feature, every subject.`}>
                {trial.headline}
              </p>
              )}
            </div>
            {/* sm–2xl (640–1535px) — wordmark + short chip inline */}
            <div className="hidden sm:flex 2xl:hidden items-center gap-1.5">
              <p className="font-heading text-sm font-bold text-slate-900 leading-tight">Gurukul Veda</p>
              {trial.bannerEnabled !== false && (
                <span className="text-[9px] font-heading font-bold uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px]" title={trial.headline}>{trial.chip}</span>
              )}
            </div>
            {/* Mobile: tiny chip only */}
            {trial.bannerEnabled !== false && (
            <span
              data-testid="beta-badge-header-mobile"
              className="sm:hidden inline-block text-[9px] font-heading font-bold tracking-wider uppercase text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-[1px]"
              title={trial.headline}>
              {trial.chip}
            </span>
            )}
          </div>

          {/* Context Dropdowns — Iter 60g (Jul 2026): compact chips on
              mobile so all four fit on one row without wrapping the whole
              header. Board + Class are static display-only so they can
              shrink safely; Subject + Topic keep tap targets big enough. */}
          <div className="flex items-center gap-1 sm:gap-2 flex-wrap w-full sm:w-auto order-3 sm:order-none mt-1 sm:mt-0">
            {/* Board */}
            <div className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-50 rounded-lg border border-slate-200 text-[11px] sm:text-sm font-body text-slate-700 flex items-center gap-1 sm:gap-1.5" data-testid="header-board">
              {shortBoardName(ctxBoard)} <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400" />
            </div>
            {/* Class */}
            <div className="px-2 sm:px-3 py-1 sm:py-1.5 bg-slate-50 rounded-lg border border-slate-200 text-[11px] sm:text-sm font-body text-slate-700 flex items-center gap-1 sm:gap-1.5" data-testid="header-class">
              {ctxClass ? `Class ${ctxClass}` : 'Class'} <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400" />
            </div>
            {/* Subject Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-2 sm:px-3 py-1 sm:py-1.5 bg-blue-50 rounded-lg border border-blue-200 text-[11px] sm:text-sm font-body text-blue-700 font-medium flex items-center gap-1 sm:gap-1.5 hover:bg-blue-100 transition-colors max-w-[110px] sm:max-w-none" data-testid="header-subject-trigger">
                  <span className="truncate">{activeSubject || 'Subject'}</span>
                  <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-400 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-xl">
                {subjects.length === 0 && subjectsLoadError && (
                  <DropdownMenuItem
                    onClick={() => setReloadKey(k => k + 1)}
                    className="font-body text-sm cursor-pointer text-rose-600"
                    data-testid="subject-load-retry"
                  >
                    Couldn't load subjects · Retry
                  </DropdownMenuItem>
                )}
                {subjects.length === 0 && !subjectsLoadError && (
                  <DropdownMenuItem disabled className="font-body text-xs text-slate-400" data-testid="subject-empty">
                    Loading…
                  </DropdownMenuItem>
                )}
                {subjects.map(s => (
                  <DropdownMenuItem key={s.subject_id} onClick={() => handleSubjectChange(s.name)}
                    className="font-body text-sm cursor-pointer" data-testid={`subject-option-${s.name.toLowerCase().replace(/\s/g, '-')}`}>
                    {s.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Chapter Dropdown — always visible; empty state shows a
                helpful message so users understand it's not broken and
                admins see they need to upload content. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="px-2 sm:px-3 py-1 sm:py-1.5 bg-emerald-50 rounded-lg border border-emerald-200 text-[11px] sm:text-sm font-body text-emerald-700 font-medium flex items-center gap-1 sm:gap-1.5 hover:bg-emerald-100 transition-colors max-w-[140px] sm:max-w-[200px]" data-testid="header-topic-trigger">
                  <span className="truncate">{activeTopic || (topics.length === 0 ? 'No chapters' : 'Chapter')}</span>
                  <ChevronDown className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="rounded-xl max-h-[300px] overflow-y-auto">
                {topics.length === 0 && (
                  <DropdownMenuItem disabled className="font-body text-xs text-slate-400" data-testid="chapters-empty">
                    No chapters uploaded yet for this subject
                  </DropdownMenuItem>
                )}
                {topics.map(t => (
                  <DropdownMenuItem key={t.topic_id} onClick={() => handleTopicChange(t.name)}
                    className="font-body text-sm cursor-pointer" data-testid={`topic-option-${t.topic_id}`}>
                    {t.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right Side: Nav + User */}
          <div className="ml-auto flex items-center gap-1">
            {/* Nav Items — Iter 60i: icon-only until 2xl so the whole
                ribbon (logo + filters + nav + refer + tier + avatar) fits
                on ONE row at 100% zoom on typical 1440–1920px displays. */}
            <nav className="hidden md:flex items-center gap-0.5 mr-2 lg:mr-3" data-testid="top-nav">
              {navItems.map(item => (
                <Tooltip key={item.id}>
                  <TooltipTrigger asChild>
                    <button
                      data-testid={`nav-${item.id}`}
                      onClick={() => item.id === 'home' ? handleGoHome() : navigate(item.path)}
                      className={`flex items-center gap-1.5 px-2 lg:px-2.5 2xl:px-3 py-1.5 rounded-lg text-sm font-body transition-all ${
                        activeNav === item.id
                          ? 'bg-blue-50 text-blue-600 font-medium'
                          : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                      }`}
                    >
                      <item.icon className={item.chest ? 'w-7 h-7' : 'w-4 h-4'} shake={item.chest && chest.shake} coins={item.chest ? chest.coins : 0} />
                      {!item.chest && <span className="hidden 2xl:inline">{item.label}</span>}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="2xl:hidden">
                    <p>{item.label}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </nav>

            {/* Mobile Nav Toggle */}
            <div className="flex md:hidden items-center gap-0.5 mr-2">
              {navItems.slice(0, 4).map(item => (
                <button key={item.id} data-testid={`nav-mobile-${item.id}`}
                  onClick={() => item.id === 'home' ? handleGoHome() : navigate(item.path)}
                  className={`p-2 rounded-lg transition-all ${activeNav === item.id ? 'bg-blue-50 text-blue-600' : 'text-slate-400'}`}>
                  <item.icon className={item.chest ? 'w-7 h-7' : 'w-4 h-4'} shake={item.chest && chest.shake} coins={item.chest ? chest.coins : 0} />
                </button>
              ))}
            </div>

            {/* Subscription Tier Pill — click to view/upgrade */}
            <ReferEarnPill />
            {(() => {
              // During a trial the user carries full (elite) privileges, but the
              // upsell logic must still treat them as their real purchased tier.
              const trialing = !!user?.trial_entitlement;
              const tier = trialing ? (user?.paid_tier || 'basic') : (user?.subscription_tier || 'basic');
              const tierMeta = {
                basic:           { label: 'Free Trial', icon: Zap, bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-200' },
                basic_lifetime:  { label: 'Basic Lifetime', icon: Sparkles, bg: 'bg-gradient-to-r from-slate-100 to-slate-200', text: 'text-slate-800', ring: 'ring-slate-300' },
                growth:          { label: 'Growth', icon: Star, bg: 'bg-gradient-to-r from-emerald-100 to-blue-100', text: 'text-emerald-700', ring: 'ring-emerald-200' },
                premium:         { label: 'Lifetime', icon: Crown, bg: 'bg-gradient-to-r from-purple-100 to-fuchsia-100', text: 'text-purple-700', ring: 'ring-purple-200' },
                elite:           { label: 'Lifetime', icon: Crown, bg: 'bg-gradient-to-r from-purple-100 to-fuchsia-100', text: 'text-purple-700', ring: 'ring-purple-200' },
              };
              const meta = tierMeta[tier] || tierMeta.basic;
              const TierIcon = meta.icon;
              const tierRank = { basic: 0, basic_lifetime: 0.5, growth: 1, premium: 2, elite: 3 };
              const myRank = tierRank[tier] ?? 0;
              const visibleEntries = Object.entries(visibleTiers || {}).filter(([, v]) => (v.tier_status || 'visible') === 'visible');
              const visibleKeys = visibleEntries.map(([k]) => k);
              const higherVisible = visibleKeys.some(k => (tierRank[k] ?? 0) > myRank);
              // Admin-enabled plans above the current one, cheapest first. On the
              // free trial the student's PAID tier is the floor, so a trialling
              // student still sees everything they can move up to.
              const upgradeTiers = visibleEntries
                .filter(([k]) => (tierRank[k] ?? 0) > myRank)
                .sort((a, b) => (tierRank[a[0]] ?? 0) - (tierRank[b[0]] ?? 0));
              // Basic-lifetime (₹49) upsell retired — only Growth is offered now.
              const showLifetimeUpgrade = false;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid="subscription-pill"
                      className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 rounded-full ${meta.bg} ${meta.text} ring-1 ${meta.ring} text-xs font-heading font-semibold hover:shadow-sm transition-all mr-1`}
                      title={`Current plan: ${meta.label}`}>
                      <TierIcon className="w-3 h-3 flex-shrink-0" />
                      {/* Iter 60i — label only on 2xl+. */}
                      <span className="hidden 2xl:inline">{meta.label}</span>
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-xl w-64 p-1" data-testid="subscription-menu">
                    <div className="px-3 py-2.5 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg ${meta.bg} flex items-center justify-center`}>
                          <TierIcon className={`w-4 h-4 ${meta.text}`} />
                        </div>
                        <div>
                          <p className="font-heading text-sm font-bold text-slate-800" data-testid="current-plan-name">
                            {trialing ? 'Free Trial' : `${meta.label} Plan`}
                          </p>
                          <p className="text-[10px] text-slate-400 font-body">
                            {trialing
                              ? (trial.label
                                ? `Full access ends ${trial.label}${visibleTiers?.[tier]?.name && tier !== 'basic' ? ` · you keep ${visibleTiers[tier].name} after that` : ' — pick a plan to continue'}`
                                : 'Subscribe to continue after the trial')
                              : `Your current subscription${visibleTiers?.[tier]?.price_label ? ` · ${visibleTiers[tier].price_label}` : ''}`}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Every plan an admin has switched on that sits ABOVE the
                        current one — with the real price from the Command Center,
                        not a number hardcoded here. During the free trial a
                        student may jump straight to any of them. */}
                    {upgradeTiers.map(([key, plan]) => {
                      const PlanIcon = key === 'growth' ? Star : Crown;
                      const toCheckout = key === 'growth' && growthSubUrl
                        ? () => window.open(growthSubUrl, '_blank', 'noopener,noreferrer')
                        : () => navigate('/subscription');
                      return (
                        <DropdownMenuItem key={key} onClick={toCheckout} data-testid={`upgrade-to-${key}-menu`}
                          className="font-body text-sm cursor-pointer py-2.5">
                          <PlanIcon className={`w-3.5 h-3.5 mr-2 flex-shrink-0 ${key === 'growth' ? 'text-emerald-600' : 'text-purple-600'}`} />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-800">
                              {trialing ? `Move to ${plan.name}` : `Upgrade to ${plan.name}`}
                            </p>
                            <p className="text-[10px] text-slate-400 truncate">
                              {plan.price_label}
                              {(plan.features || []).length ? ` · ${(plan.features || []).slice(0, 2).join(', ')}` : ''}
                            </p>
                          </div>
                        </DropdownMenuItem>
                      );
                    })}
                    {upgradeTiers.length ? (
                      <DropdownMenuItem onClick={() => navigate('/subscription')} className="font-body text-sm cursor-pointer" data-testid="view-all-plans-menu">
                        <Sparkles className="w-3.5 h-3.5 mr-2 text-blue-500" /> See all plans and prices
                      </DropdownMenuItem>
                    ) : (
                      <div className="px-3 py-2 text-[11px] text-slate-400 font-body" data-testid="no-upgrades-notice">
                        You're on the highest plan available right now. More plans launching soon.
                      </div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })()}

            {/* Notifications + Parent dashboard risk ring */}
            {(user?.role === 'admin' || user?.role === 'support_staff') && <AdminTasksButton />}
            <NotificationBell />

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-50 transition-colors" data-testid="user-menu-trigger">
                  <Avatar className={`w-11 h-11 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 border ${planRisk === 'red' ? 'border-rose-400 ring-2 ring-rose-300' : planRisk === 'orange' ? 'border-amber-400 ring-2 ring-amber-300' : 'border-slate-200'}`}
                    data-testid={`avatar-risk-${planRisk}`}>
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-blue-50 text-blue-600 text-sm font-heading">
                      {user?.name?.[0] || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl w-60">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="font-heading text-sm font-semibold text-slate-800 truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 font-body truncate">{user?.email}</p>
                </div>
                {planRisk !== 'green' && (
                  <DropdownMenuItem onClick={() => setDashOpen(true)}
                    className={`font-body text-sm cursor-pointer ${planRisk === 'red' ? 'text-rose-700' : 'text-amber-700'}`}
                    data-testid="menu-urgent-attention">
                    <AlertTriangle className="w-3.5 h-3.5 mr-2" />
                    {planRisk === 'red' ? 'Urgent: study plan too heavy' : 'Study plan is getting tight'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => setDashOpen(true)} className="font-body text-sm cursor-pointer" data-testid="menu-parent-dashboard">
                  <Gauge className="w-3.5 h-3.5 mr-2" /> Parent dashboard
                </DropdownMenuItem>
                {(roster?.profiles || []).length > 0 && (
                  <>
                    <p className="px-3 pt-2 pb-1 text-[10px] font-heading uppercase tracking-widest text-slate-400">Studying as</p>
                    {roster.profiles.map(p => (
                      <DropdownMenuItem key={p.profile_id} onClick={() => switchChild(p.profile_id)}
                        className="font-body text-sm cursor-pointer gap-2" data-testid={`switch-child-${p.profile_id}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${p.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <span className="min-w-0 flex-1 truncate">
                          {p.name}
                          <span className="block text-[10px] text-slate-400 leading-tight">
                            {(p.board || '').toUpperCase()} · Class {p.class_level || '—'}{p.kind === 'linked' ? ' · own login' : ''}
                          </span>
                        </span>
                        {p.is_active && <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem onClick={() => setAddChildOpen(true)} className="font-body text-sm cursor-pointer" data-testid="menu-add-child">
                  <UserPlus className="w-3.5 h-3.5 mr-2" /> Add a child
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setThemeOpen(true)} className="font-body text-sm cursor-pointer" data-testid="menu-theme">
                  <Palette className="w-3.5 h-3.5 mr-2" /> Study theme
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/syllabus')} className="font-body text-sm cursor-pointer" data-testid="menu-syllabus">
                  <BookMarked className="w-3.5 h-3.5 mr-2" /> Syllabus
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/exams')} className="font-body text-sm cursor-pointer" data-testid="menu-exams">
                  <GraduationCap className="w-3.5 h-3.5 mr-2" /> Exam
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="font-body text-sm cursor-pointer" data-testid="menu-profile">
                  <CircleUserRound className="w-3.5 h-3.5 mr-2" /> Profile &amp; Stats
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="font-body text-sm cursor-pointer" data-testid="menu-admin">
                    <Shield className="w-3.5 h-3.5 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="font-body text-sm cursor-pointer text-red-500" data-testid="menu-logout">
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Main Content — Iter 60j: `relative` anchors absolutely-
            positioned children (HomePage / ChatPage use `absolute inset-0`
            to fill the visible viewport regardless of browser zoom so
            their bottom composer is always at the true viewport bottom).
            Non-chat pages render as normal block flow and scroll via
            main's `overflow-auto`. */}
        <main className="flex-1 relative overflow-auto">
          {typeof children === 'function' ? children({ chatResetKey, activeTopic, onSessionLoad }) : children}
        </main>
      </div>

      <AlertDialog open={!!pendingNav} onOpenChange={(o) => { if (!o) setPendingNav(null); }}>
        <AlertDialogContent data-testid="puzzle-leave-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Change {pendingNav?.type === 'subject' ? 'subject' : 'chapter'}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              You're changing the {pendingNav?.type === 'subject' ? 'subject' : 'chapter'}
              {pendingNav?.value ? <> to <strong className="text-slate-900">{pendingNav.value}</strong></> : ''}.
              This will close the current puzzle and take you back to Home. Your progress on this puzzle won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="puzzle-leave-continue">Continue puzzle</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPendingNav} data-testid="puzzle-leave-change"
              className="bg-blue-600 hover:bg-blue-700">
              Change &amp; go Home
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AddChildDialog open={addChildOpen} onOpenChange={setAddChildOpen}
        chargeNotice={roster?.charge_notice} canAdd={!!roster?.can_add}
        tierName={roster?.tier_name} maxChildren={roster?.max_children ?? 0}
        onAdded={() => loadRoster()} />

      <ThemeDialog open={themeOpen} onOpenChange={setThemeOpen}
        target={activeProfile || { kind: 'self', name: user?.name, theme: roster?.self?.theme }}
        onSaved={() => loadRoster()} />

      <ParentDashboardDialog open={dashOpen} onOpenChange={setDashOpen} />
    </TooltipProvider>
  );
}
